/**
 * Collapsible JSON Viewer - Shared Component
 * A reusable component for displaying JSON data with expand/collapse functionality
 */

import { Box, Typography, Paper, Button, Stack, useTheme } from '@mui/material';
import { useState, useEffect } from 'react';

interface CollapsibleJsonViewerProps {
  data: any;
  title?: string;
  maxHeight?: string | number;
  defaultExpandTopLevel?: boolean; // New prop to control top-level expansion
}

export default function CollapsibleJsonViewer({ 
  data, 
  title,
  maxHeight = '600px',
  defaultExpandTopLevel = true 
}: CollapsibleJsonViewerProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  // Theme-aware colors
  const colors = {
    background: isDark ? '#1E1E1E' : '#F8F9FA',
    paper: isDark ? '#252526' : '#FFFFFF',
    paperHover: isDark ? '#383838' : '#F3F4F6',
    header: isDark ? '#2D2D2D' : '#E5E7EB',
    border: isDark ? '#3f3f3f' : '#D1D5DB',
    text: isDark ? '#D4D4D4' : '#1F2937',
    textSecondary: isDark ? '#808080' : '#6B7280',
    key: isDark ? '#9CDCFE' : '#2563EB',
    string: isDark ? '#CE9178' : '#059669',
    number: isDark ? '#B5CEA8' : '#DC2626',
    boolean: isDark ? '#569CD6' : '#7C3AED',
    expand: isDark ? '#4EC9B0' : '#0891B2',
    buttonBg: isDark ? '#3C3C3C' : '#E5E7EB',
    buttonText: isDark ? '#CCCCCC' : '#374151',
    buttonHover: isDark ? '#505050' : '#D1D5DB',
  };

  // Initialize top-level keys as expanded/collapsed based on prop
  useEffect(() => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const initialState: { [key: string]: boolean } = {};
      Object.keys(data).forEach(key => {
        initialState[`root.${key}`] = defaultExpandTopLevel;
      });
      setExpanded(initialState);
    }
  }, [data, defaultExpandTopLevel]);

  const toggleExpand = (path: string) => {
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const expandAll = () => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const allExpanded: { [key: string]: boolean } = {};
      Object.keys(data).forEach(key => {
        allExpanded[`root.${key}`] = true;
      });
      setExpanded(allExpanded);
    }
  };

  const collapseAll = () => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const allCollapsed: { [key: string]: boolean } = {};
      Object.keys(data).forEach(key => {
        allCollapsed[`root.${key}`] = false;
      });
      setExpanded(allCollapsed);
    }
  };

  const renderValue = (value: any, path: string = '', depth: number = 0, isTopLevel: boolean = false): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span style={{ color: colors.textSecondary }}>null</span>;
    }

    if (typeof value === 'boolean') {
      return <span style={{ color: colors.boolean }}>{String(value)}</span>;
    }

    if (typeof value === 'number') {
      return <span style={{ color: colors.number }}>{value}</span>;
    }

    if (typeof value === 'string') {
      // For top-level string values, render as formatted text in a box
      if (isTopLevel) {
        return (
          <Box 
            sx={{ 
              mt: 1,
              p: 1.5, 
              bgcolor: colors.header, 
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: colors.text,
              fontSize: '0.875rem',
              lineHeight: 1.6,
              fontFamily: 'inherit'
            }}
          >
            {value}
          </Box>
        );
      }
      return <span style={{ color: colors.string, fontFamily: 'monospace' }}>"{value}"</span>;
    }

    if (Array.isArray(value)) {
      const isExpanded = expanded[path];
      if (value.length === 0) {
        return <span>[]</span>;
      }
      return (
        <Box component="span">
          <span
            onClick={() => toggleExpand(path)}
            style={{ cursor: 'pointer', userSelect: 'none', color: colors.expand }}
          >
            {isExpanded ? '▼' : '▶'} Array({value.length})
          </span>
          {isExpanded && (
            <Box sx={{ ml: 2, borderLeft: `1px solid ${colors.border}`, pl: 1 }}>
              {value.map((item, index) => (
                <Box key={index} sx={{ my: 0.5 }}>
                  <span style={{ color: colors.textSecondary }}>[{index}]: </span>
                  {renderValue(item, `${path}.${index}`, depth + 1, false)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    if (typeof value === 'object') {
      const isExpanded = expanded[path];
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <span>{'{}'}</span>;
      }
      return (
        <Box component="span">
          <span
            onClick={() => toggleExpand(path)}
            style={{ cursor: 'pointer', userSelect: 'none', color: colors.expand }}
          >
            {isExpanded ? '▼' : '▶'} Object({keys.length})
          </span>
          {isExpanded && (
            <Box sx={{ ml: 2, borderLeft: `1px solid ${colors.border}`, pl: 1 }}>
              {keys.map((key) => (
                <Box key={key} sx={{ my: 0.5 }}>
                  <span style={{ color: colors.key }}>{key}</span>
                  <span>: </span>
                  {renderValue(value[key], `${path}.${key}`, depth + 1, false)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    return <span>{String(value)}</span>;
  };

  // Special rendering for top-level object with individual collapsible sections
  const renderTopLevelObject = () => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return renderValue(data, 'root', 0, false);
    }

    const keys = Object.keys(data);
    if (keys.length === 0) {
      return <span>{'{}'}</span>;
    }

    return (
      <Box>
        {keys.map((key) => {
          const path = `root.${key}`;
          const isExpanded = expanded[path] !== undefined ? expanded[path] : defaultExpandTopLevel;
          const value = data[key];

          return (
            <Paper
              key={key}
              sx={{
                mb: 1.5,
                bgcolor: colors.paper,
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {/* Collapsible header */}
              <Box
                onClick={() => toggleExpand(path)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  bgcolor: colors.header,
                  '&:hover': {
                    bgcolor: colors.paperHover,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <span style={{ color: colors.expand, fontSize: '1rem' }}>
                  {isExpanded ? '▼' : '▶'}
                </span>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colors.key,
                    fontWeight: 600,
                  }}
                >
                  {key}
                </Typography>
              </Box>

              {/* Collapsible content */}
              {isExpanded && (
                <Box sx={{ p: 1.5, pt: 0.5 }}>
                  {renderValue(value, path, 1, true)}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  return (
    <Paper 
      sx={{ 
        p: 2, 
        bgcolor: colors.background, 
        color: colors.text, 
        fontSize: '0.875rem' 
      }}
    >
      {/* Header with title and control buttons */}
      {title && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ color: colors.text, fontWeight: 600 }}>
            {title}
          </Typography>
          {data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0 && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={expandAll}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  bgcolor: colors.buttonBg,
                  color: colors.buttonText,
                  '&:hover': {
                    bgcolor: colors.buttonHover,
                  },
                }}
              >
                📂 Expand All
              </Button>
              <Button
                size="small"
                onClick={collapseAll}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  bgcolor: colors.buttonBg,
                  color: colors.buttonText,
                  '&:hover': {
                    bgcolor: colors.buttonHover,
                  },
                }}
              >
                📁 Collapse All
              </Button>
            </Stack>
          )}
        </Box>
      )}
      
      <Box sx={{ overflow: 'auto', maxHeight }}>
        {renderTopLevelObject()}
      </Box>
    </Paper>
  );
}
